import LinkButton from 'components/shared/button/LinkButton';
import classes from './TileReorder.module.scss';
import GNEModal from 'components/shared/modal/Modal';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { DndProvider, useDrag, useDragLayer, useDrop } from 'react-dnd';
import { getTilesToReorder } from 'store/home/home-my-view-actions';
import { updatePositionIndices } from 'utils/utils';
import { MultiBackend } from 'dnd-multi-backend';
import { HTML5toTouch } from 'rdndmb-html5-to-touch';
import { getEmptyImage } from 'react-dnd-html5-backend';

const ItemType = { TILE: 'tile' };

const Tile = ({ tile, index, moveTile }) => {
  const ref = useRef(null);
  const handleRef = useRef(null);
  const [{ isOver }, drop] = useDrop({
    accept: ItemType.TILE,
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
    hover: (draggedItem, monitor) => {
      if (!ref.current) return;

      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Prevent swap until cursor crosses the middle of the hovered tile
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      // Dragging down but not past halfway
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveTile(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    },
  });

  const [, drag, dragPreview] = useDrag({
    type: ItemType.TILE,
    item: { index, tile },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  drop(ref);
  drag(handleRef);

  return (
    <div
      ref={ref}
      className={classes['tile-item']}>
      <div className={classes['tile-number-block']}>
        <span className={classes.indexCircle}>{index + 1}</span>
      </div>

      <div
        ref={handleRef}
        className={classes['dragable-tile-content']}>
        <i className='icon icon__grip-vertical ms-2'></i>
        <span className='text text__small ms-2'>{tile.title}</span>
      </div>

      {isOver && <div className={classes['drop-indicator']}></div>}
    </div>
  );
};

const CustomDragLayer = () => {
  const { item, initialOffset, currentOffset, isDragging } = useDragLayer(
    (monitor) => ({
      item: monitor.getItem(),
      itemType: monitor.getItemType(),
      initialOffset: monitor.getInitialSourceClientOffset(),
      currentOffset: monitor.getSourceClientOffset(),
      isDragging: monitor.isDragging(),
    })
  );

  if (!isDragging) {
    return null;
  }

  const getItemStyles = (initialOffset, currentOffset) => {
    if (!initialOffset || !currentOffset) {
      return { display: 'none' };
    }
    const { x, y } = currentOffset;
    const transform = `translate(${x}px, ${y}px)`;
    return {
      transform,
      WebkitTransform: transform,
    };
  };

  const renderItem = () => (
    <div className={`${classes['tile-item']} ${classes.dragging}`}>
      <div className={classes['dragable-tile-content']}>
        <i className='icon icon__grip-vertical'></i>
        <span className='text text__small'>{item.tile.title}</span>
      </div>
    </div>
  );

  return (
    <div className={classes['custom-drag-layer']}>
      <div style={getItemStyles(initialOffset, currentOffset)}>
        {renderItem()}
      </div>
    </div>
  );
};

const TileReorder = ({ page, selectedView, show, onHide, onSave }) => {
  const [hasReordered, setHasReordered] = useState(false);
  const [reorderedTiles, setReorderedTiles] = useState([]);

  useEffect(() => {
    const fetchTiles = async () => {
      const tilesToReorder = await getTilesToReorder(page, selectedView?.Id);
      setReorderedTiles(tilesToReorder);
    };
    fetchTiles();
  }, []);
  const moveTile = (fromIndex, toIndex) => {
    const reindexedTiles = updatePositionIndices(
      fromIndex,
      toIndex,
      reorderedTiles
    );
    setReorderedTiles(reindexedTiles);
    setHasReordered(true);
  };
  useEffect(() => {
    const container = document.querySelector(`.${classes['tile-list']}`);

    const edge = 60,
      speed = 10;

    const onTouchMove = ({ touches }) => {
      const { top, bottom } = container.getBoundingClientRect();
      const y = touches[0]?.clientY;
      if (y > bottom - edge) container.scrollTop += speed;
      else if (y < top + edge) container.scrollTop -= speed;
    };

    document.addEventListener('touchmove', onTouchMove);
    return () => document.removeEventListener('touchmove', onTouchMove);
  }, []);

  const titleContent = (
    <div className='m-4 mb-1 mt-1'>
      <div className='d-flex align-items-center'>
        <i className='icon icon__table-cells mb-2'></i>
        <span className='title title__bold mb-2'>Reorder Tile</span>
      </div>
      <p className='text text__small'>
        Tiles are arranged sequentially from left to right, moving row by row
        from top to bottom. To rearrange, simply drag a tile and place it either
        before or after another tile in the desired position.
      </p>
    </div>
  );

  const modalContent = (
    <div className={`${classes['tile-list']} mt-2`}>
      {reorderedTiles?.length > 0 &&
        reorderedTiles?.map((tile, index) => (
          <Tile
            key={tile.cardId}
            tile={tile}
            index={index}
            moveTile={moveTile}
          />
        ))}
    </div>
  );

  const footer = (
    <div className='d-flex justify-content-end w-100 me-2'>
      <LinkButton
        className='text text__small'
        onClick={onHide}>
        Cancel
      </LinkButton>
      <Button
        variant='btn btn-gne btn-primary m-2'
        onClick={(e) => {
          e.preventDefault();
          onSave(reorderedTiles);
        }}
        disabled={!hasReordered}>
        Save
      </Button>
    </div>
  );

  return (
    <DndProvider
      backend={MultiBackend}
      options={HTML5toTouch}>
      <GNEModal
        show={show}
        onHide={onHide}
        titlecontent={titleContent}
        content={modalContent}
        css='size-medium'
        backdrop='static'
        footer={footer}
      />
      <CustomDragLayer />
    </DndProvider>
  );
};

export default TileReorder;
